import React, { useEffect, useMemo, useState } from "react";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import moment from "moment";
import "bootstrap/dist/css/bootstrap.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faTrash } from "@fortawesome/free-solid-svg-icons";

import {
  useTable,
  useSortBy,
  useGlobalFilter,
  usePagination,
} from "react-table";

const DonationLetterList = () => {
  const [data, setData] = useState([]);
  const [receiptMap, setReceiptMap] = useState({});
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem("token");
  const centerCode = localStorage.getItem("code");
  const sewadarId = localStorage.getItem("sewadarId");

  const navigate = useNavigate();

  /* ---------------- PREFIX ---------------- */
  const getDonationPrefix = (date) => {
    const year = date ? moment(date).year() : moment().year();
    return `F.142/RSSB/JP/${year}/`;
  };

  /* ---------------- FETCH DONATIONS ---------------- */
  const fetchLetters = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(
        `${SERVER_BASE}/getDonationLetters`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(res.data || []);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to load donation letters", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- FETCH RECEIPTS ---------------- */
  const fetchReceiptVouchers = async () => {
    try {
      const res = await apiClient.get(
        `${SERVER_BASE}/getReceiptVouchers/${centerCode}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const map = {};
      (res.data || []).forEach((r) => {
        map[r.id] = r.ledgerName;
      });
      setReceiptMap(map);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchLetters();
    fetchReceiptVouchers();
  }, []);

  /* ---------------- DELETE ---------------- */
  const handleDelete = async (row) => {
    const res = await Swal.fire({
      title: "Delete Donation Letter?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete",
    });

    if (!res.isConfirmed) return;

    try {
      await apiClient.delete(
        `${SERVER_BASE}/deleteDonationLetter/${row.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      Swal.fire("Deleted", "Donation letter deleted", "success");
      fetchLetters();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to delete donation", "error");
    }
  };

  /* ---------------- COLUMNS ---------------- */
  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: (_, i) => i + 1,
        disableSortBy: true,
        width: 40,
      },
      {
        Header: "Date",
        accessor: "header.date",
        Cell: ({ value }) =>
          value ? moment(value).format("DD MMM YYYY") : "—",
      },
      {
        Header: "Form No",
        accessor: "header.formNo",
        Cell: ({ row }) => {
          const date = row.original.header?.date;
          return (
            <span>
              {getDonationPrefix(date)}
              {row.original.header?.formNo || ""}
            </span>
          );
        },
      },
      {
        Header: "Ledger",
        accessor: "receiptId",
        Cell: ({ value }) =>
          receiptMap[value] || <span className="text-muted">—</span>,
      },
      {
        Header: "Items",
        accessor: "items",
        disableSortBy: true,
        Cell: ({ value }) =>
          Array.isArray(value) ? value.length : 0,
      },
      {
        Header: "Donors",
        accessor: "donors",
        disableSortBy: true,
        Cell: ({ value }) =>
          Array.isArray(value) ? value.length : 0,
      },
      {
        Header: "Created",
        accessor: "creator",
        Cell: ({ row }) => {
          const c = row.original.creator;
          return (
            <span>
              {c ? `${c.firstName} ${c.lastName}` : "—"}
              <br />
              {row.original.createdAt
                ? moment(row.original.createdAt).format(
                    "DD MMM YY hh:mm A"
                  )
                : ""}
            </span>
          );
        },
      },
      {
        Header: "Updated",
        accessor: "updater",
        Cell: ({ row }) => {
          const u = row.original.updater;
          return (
            <span>
              {u ? `${u.firstName} ${u.lastName}` : "—"}
              <br />
              {row.original.updatedAt
                ? moment(row.original.updatedAt).format(
                    "DD MMM YY hh:mm A"
                  )
                : ""}
            </span>
          );
        },
      },
      {
        Header: "Actions",
        disableSortBy: true,
        Cell: ({ row }) => (
          <div className="text-center">
            <button
              className="btn btn-sm btn-outline-success"
              onClick={() =>
                navigate("/DonationPreview", {
                  state: { donationId: row.original.id },
                })
              }
            >
              View
            </button>

            <button
              className="btn btn-sm btn-outline-primary ms-2"
              onClick={() =>
                navigate("/donationForm", {
                  state: { donationId: row.original.id },
                })
              }
            >
              <FontAwesomeIcon icon={faPencil} />
            </button>

            {sewadarId === "23" && (
              <button
                className="btn btn-sm btn-outline-danger ms-2"
                onClick={() => handleDelete(row.original)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            )}
          </div>
        ),
      },
    ],
    [receiptMap]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    state,
    setGlobalFilter,
    setPageSize,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageSize: 20 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { globalFilter, pageIndex, pageSize } = state;

  return (
    <div className="formcontainer mt-5">
      <h5>Donation Letters</h5>

      {/* TOP BAR */}
      <div className="row mb-3">
        <div className="col-md-3">
          <button
            className="btn btn-outline-success"
            onClick={() => navigate("/donationForm")}
          >
            Generate Donation
          </button>
        </div>

        <div className="col-md-7">
          <input
            className="form-control"
            placeholder="Search..."
            value={globalFilter || ""}
            onChange={(e) => setGlobalFilter(e.target.value)}
          />
        </div>

        <div className="col-md-2">
          <select
            className="form-select"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {[20, 50, 100, 200].map((s) => (
              <option key={s} value={s}>
                Show {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* TABLE */}
      <table
        className="table table-bordered table-hover align-middle"
        {...getTableProps()}
        style={{ fontSize: "12px" }}
      >
        <thead className="table-light">
          {headerGroups.map((hg) => (
            <tr {...hg.getHeaderGroupProps()}>
              {hg.headers.map((col) => (
                <th {...col.getHeaderProps(col.getSortByToggleProps())}>
                  {col.render("Header")}
                  {col.isSorted &&
                    (col.isSortedDesc ? " 🔽" : " 🔼")}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {loading ? (
            <tr>
              <td colSpan={columns.length} className="text-center">
                Loading...
              </td>
            </tr>
          ) : page.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="text-center">
                No donation letters found
              </td>
            </tr>
          ) : (
            page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()}>
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {/* PAGINATION */}
      <div className="d-flex align-items-center mb-3">
        <button
          onClick={() => previousPage()}
          disabled={!canPreviousPage}
          className="btn btn-secondary me-auto"
        >
          Previous
        </button>

        <div className="fw-semibold">
          Page {pageIndex + 1} of {pageOptions.length}
        </div>

        <button
          onClick={() => nextPage()}
          disabled={!canNextPage}
          className="btn btn-secondary ms-auto"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default DonationLetterList;
